import { ChangeEvent, useEffect, useState } from "react";
import StatusBadge from "./StatusBadge";
import StageOutOfScopeMessage from "./StageOutOfScopeMessage";
import {
  AUTH_TYPE,
  ConnectionTestResult,
  STAGE_DESCRIPTIONS,
  STAGE_META,
  ServiceProvider,
  StageConfig,
} from "../../../utils/integrationConfigData";
import {
  AuthTypeApi,
  ConfigurationTypeApi,
  mapIntegrationToStageConfig,
} from "../types/providerIntegrationtypes";
import { isTestSuccess } from "../../../constants/integrationKeys";
import {
  formatTestResultMessage,
  saveStageConfig,
} from "../../../utils/integrationApiHelpers";
import { useAuditUserId } from "../../../hooks/useAuditUserId";

interface StagePasSftpFormProps {
  provider: ServiceProvider;
  stage?: StageConfig & { configurationId?: number; isDeleted?: boolean };
  showInfoBanner?: boolean;
  authTypes?: AuthTypeApi[];
  configurationTypes: ConfigurationTypeApi[];
  selectedConfigurationTypeId: number | null;
  onConfigurationTypeChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onUseHttp: () => void;
  isLoading?: boolean;
  loadError?: string | null;
  onProvidersChanged?: () => void;
  onSaved?: (stage: StageConfig & { configurationId?: number }) => void;
}

type SftpFormState = {
  sftpHost: string;
  sftpPort: string;
  sftpUsername: string;
  sftpPassword: string;
  sftpPath: string;
};

const buildSftpFormState = (stage?: StageConfig): SftpFormState => ({
  sftpHost: stage?.sftpHost || "",
  sftpPort: stage?.sftpPort || "22",
  sftpUsername: stage?.sftpUsername || "",
  sftpPassword: stage?.sftpPassword || "",
  sftpPath: stage?.sftpPath || "",
});

const StagePasSftpForm = ({
  provider,
  stage,
  showInfoBanner = true,
  authTypes = [],
  configurationTypes,
  selectedConfigurationTypeId,
  onConfigurationTypeChange,
  onUseHttp,
  isLoading = false,
  loadError = null,
  onProvidersChanged,
  onSaved,
}: StagePasSftpFormProps) => {
  const meta = STAGE_META.pas;
  const description = stage?.description || STAGE_DESCRIPTIONS.pas;
  const auditUserId = useAuditUserId();

  const [form, setForm] = useState<SftpFormState>(() => buildSftpFormState(stage));
  const [testResult, setTestResult] = useState<ConnectionTestResult | null>(
    stage?.testResult || null,
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setForm(buildSftpFormState(stage));
    setTestResult(stage?.testResult || null);
  }, [stage]);

  useEffect(() => {
    if (loadError) {
      setTestResult({ message: loadError, status: "error" });
    }
  }, [loadError]);

  const handleChange =
    (field: keyof SftpFormState) =>
    (event: ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      setForm((prev) => ({ ...prev, [field]: value }));
      setTestResult(null);
    };

  const getValidationError = (): string | null => {
    if (!selectedConfigurationTypeId) {
      return "Failed · Missing PAS endpoint type · 0ms";
    }
    if (!form.sftpHost.trim()) {
      return "Failed · Missing SFTP host · 0ms";
    }
    if (!form.sftpPort.trim()) {
      return "Failed · Missing SFTP port · 0ms";
    }
    if (!form.sftpUsername.trim()) {
      return "Failed · Missing SFTP username · 0ms";
    }
    if (!form.sftpPassword.trim()) {
      return "Failed · Missing SFTP password · 0ms";
    }
    if (!form.sftpPath.trim()) {
      return "Failed · Missing SFTP path · 0ms";
    }
    return null;
  };

  const handleSave = async () => {
    const validationError = getValidationError();
    if (validationError) {
      setTestResult({ message: validationError, status: "error" });
      return;
    }

    const providerId = Number(provider.id);
    if (!providerId || !selectedConfigurationTypeId) return;

    setIsSaving(true);
    try {
      const result = await saveStageConfig(
        providerId,
        "pas",
        {
          endpoint: "",
          authType: AUTH_TYPE.Basic,
          requestUrls: [],
          headers: [],
          credentialHeaders: [],
          queryParams: [],
          requestBody: [],
          useSftp: true,
          sftpHost: form.sftpHost,
          sftpPort: form.sftpPort,
          sftpUsername: form.sftpUsername,
          sftpPassword: form.sftpPassword,
          sftpPath: form.sftpPath,
        },
        authTypes,
        auditUserId,
        selectedConfigurationTypeId,
      );
      setTestResult({
        message: formatTestResultMessage(result),
        status: isTestSuccess(result.testResult?.testStatus)
          ? "success"
          : "error",
      });
      const nextStage = {
        ...mapIntegrationToStageConfig(result),
        configurationId: result.id,
      };
      onSaved?.(nextStage);
      onProvidersChanged?.();
    } catch (error: unknown) {
      const message =
        error instanceof Error ? error.message : "Failed · Save failed · 0ms";
      setTestResult({
        message: message.startsWith("Failed")
          ? message
          : `Failed · ${message} · 0ms`,
        status: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const fieldId = (name: string) => `pas-sftp-${name}`;
  const displayStatus = stage?.status || "notSet";
  const isNotSet = displayStatus === "notSet";

  return (
    <div className="ic-stage-form-panel">
      {showInfoBanner && (
        <div className="ic-info-banner" role="note">
          <i className="fh_info_fill" aria-hidden="true"></i>
          <span>
            Configure SFTP for attachment submit, then Test &amp; Save
            Configuration.
          </span>
        </div>
      )}

      <div className="ic-stage-form-shell">
        <div className="ic-stage-form-content">
          <header className="ic-stage-form-header">
            <div className="ic-stage-form-title-row">
              <span className="ic-stats-icon">
                <i className={meta.icon}></i>
              </span>
              <div className="ic-stage-form-heading-text">
                <div className="ic-stage-form-title-line">
                  <h2 className="ic-stage-form-title">{meta.label}</h2>
                  <StatusBadge status={displayStatus} />
                </div>
                <p className="ic-stage-form-desc mb-0">{description}</p>
              </div>
            </div>
          </header>

          {isLoading && (
            <div className="ic-inline-message" role="status">
              Loading PAS configuration...
            </div>
          )}

          {isNotSet && !isLoading && (
            <StageOutOfScopeMessage stageLabel={meta.label} />
          )}

          <section className="ic-stage-form-section">
            <h3 className="ic-stage-form-section-title">PAS endpoint</h3>
            <div className="ic-stage-form-field">
              <label
                className="ic-stage-form-label"
                htmlFor={fieldId("endpoint-type")}
              >
                PAS endpoint
              </label>
              <select
                id={fieldId("endpoint-type")}
                className="form-select form-select-sm ic-stage-form-input ic-stage-form-select"
                value={selectedConfigurationTypeId ?? ""}
                onChange={onConfigurationTypeChange}
                disabled={!configurationTypes.length}
              >
                {configurationTypes.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.displayName}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-check mt-2">
              <input
                type="checkbox"
                className="form-check-input"
                id={fieldId("use-sftp")}
                checked
                onChange={(event) => {
                  if (!event.target.checked) onUseHttp();
                }}
              />
              <label className="form-check-label" htmlFor={fieldId("use-sftp")}>
                SFTP
              </label>
            </div>
          </section>

          <section className="ic-stage-form-section">
            <h3 className="ic-stage-form-section-title">SFTP configuration</h3>
            <div className="ic-stage-form-grid ic-stage-form-grid-stack">
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("host")}
                >
                  Host
                </label>
                <input
                  id={fieldId("host")}
                  type="text"
                  className="form-control form-control-sm ic-stage-form-input"
                  value={form.sftpHost}
                  onChange={handleChange("sftpHost")}
                  placeholder="sftp.example.com"
                  autoComplete="off"
                />
              </div>
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("port")}
                >
                  Port
                </label>
                <input
                  id={fieldId("port")}
                  type="text"
                  className="form-control form-control-sm ic-stage-form-input"
                  value={form.sftpPort}
                  onChange={handleChange("sftpPort")}
                  placeholder="22"
                  autoComplete="off"
                />
              </div>
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("username")}
                >
                  Username
                </label>
                <input
                  id={fieldId("username")}
                  type="text"
                  className="form-control form-control-sm ic-stage-form-input"
                  value={form.sftpUsername}
                  onChange={handleChange("sftpUsername")}
                  placeholder="Enter username"
                  autoComplete="off"
                />
              </div>
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("password")}
                >
                  Password
                </label>
                <input
                  id={fieldId("password")}
                  type="password"
                  className="form-control form-control-sm ic-stage-form-input"
                  value={form.sftpPassword}
                  onChange={handleChange("sftpPassword")}
                  placeholder="Enter password"
                  autoComplete="off"
                />
              </div>
              <div className="ic-stage-form-field">
                <label
                  className="ic-stage-form-label"
                  htmlFor={fieldId("path")}
                >
                  Path
                </label>
                <input
                  id={fieldId("path")}
                  type="text"
                  className="form-control form-control-sm ic-stage-form-input"
                  value={form.sftpPath}
                  onChange={handleChange("sftpPath")}
                  placeholder="/remote/path"
                  autoComplete="off"
                />
              </div>
            </div>
          </section>

          <footer className="ic-stage-form-footer">
            <button
              type="button"
              className="btn btn-primary btn-sm ic-test-connection-btn"
              onClick={handleSave}
              disabled={isSaving || isLoading || !selectedConfigurationTypeId}
            >
              {isSaving ? (
                <>
                  <span className="ic-test-spinner" aria-hidden="true" />
                  Testing &amp; saving...
                </>
              ) : (
                "Test & Save Configuration"
              )}
            </button>

            {testResult ? (
              <div
                className={`ic-test-result ic-test-result-${testResult.status}`}
                role="status"
              >
                <i
                  className={
                    testResult.status === "error"
                      ? "fh_error_line"
                      : "fh_check_circle_line"
                  }
                  aria-hidden="true"
                ></i>
                <span>{testResult.message}</span>
              </div>
            ) : (
              <span className="ic-test-pending" role="status">
                Not tested yet
              </span>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
};

export default StagePasSftpForm;
